/**
 * Cervus Botica — editorial search overlay
 */
(function () {
  function cervusInitSearchOverlay() {
    const overlay = document.querySelector('[data-search-overlay]');
    if (!overlay) return;

    const openButtons = document.querySelectorAll('[data-search-open]');
    const closeTargets = overlay.querySelectorAll('[data-search-close]');
    const form = overlay.querySelector('[data-search-form]');
    const input = overlay.querySelector('[data-search-input]');
    const liveRoot = overlay.querySelector('[data-search-live]');
    const liveList = overlay.querySelector('[data-search-live-list]');
    const nav = document.querySelector('[data-editorial-nav]');
    const navToggle = document.querySelector('[data-nav-toggle]');
    const navPanel = document.querySelector('[data-nav-panel]');
    const body = document.body;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const desktopMq = window.matchMedia('(min-width: 990px)');
    const mobileMq = window.matchMedia('(max-width: 47.99rem)');
    const searchPath = new URL(overlay.dataset.searchPath || '/search', window.location.origin).pathname;

    let lastFocus = null;
    let suggestTimer = null;
    let suggestRequest = null;
    let isOpen = false;

    function closeNavPanel() {
      window.cervusNav?.close?.();
    }

    function syncNavSolid(open) {
      if (window.cervusNavChrome?.syncSolid) {
        window.cervusNavChrome.syncSolid(open);
        return;
      }

      if (!nav) return;

      if (open) {
        nav.classList.add('editorial-nav--solid', 'is-light-header');
        body.classList.remove('editorial-nav-is-solid');
        return;
      }

      if (body.classList.contains('template-index')) {
        body.classList.remove('editorial-nav-is-solid');

        const hero = document.querySelector('#sala-00');
        if (!hero) return;

        const raw = getComputedStyle(document.documentElement).getPropertyValue('--site-chrome-height').trim();
        const chromeHeight = Number.parseFloat(raw) || 100;
        const overHero = hero.getBoundingClientRect().bottom > chromeHeight + 4;
        if (overHero) {
          nav.classList.remove('editorial-nav--solid', 'is-light-header');
        } else {
          nav.classList.add('editorial-nav--solid', 'is-light-header');
          body.classList.add('editorial-nav-is-solid');
        }
        return;
      }

      if (nav.classList.contains('header--static')) {
        nav.classList.add('is-light-header');
        return;
      }

      nav.classList.remove('editorial-nav--solid', 'is-light-header');
    }

    function finishSearchClose(options = {}) {
      const switchContext = options.switchContext === true;

      if (switchContext) {
        window.cervusMobileDrawer?.beginClose?.(overlay);
        isOpen = false;
        overlay.setAttribute('aria-hidden', 'true');
        openButtons.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });
        return Promise.resolve();
      }

      window.cervusMobileDrawer?.closeVisual?.(overlay);
      isOpen = false;
      overlay.setAttribute('aria-hidden', 'true');
      window.cervusBodyLock?.set?.('search', false);
      openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
      syncNavSolid(false);
      if (input) input.value = '';
      hideLiveResults();
      if (lastFocus instanceof HTMLElement) {
        lastFocus.focus();
      }
      return Promise.resolve();
    }

    function setSearchOpen(open, options = {}) {
      const switchContext = options.switchContext === true;

      if (open) {
        if (!switchContext) {
          window.cervusCart?.close?.({ immediate: true });
          window.cervusNavDrawer?.close?.();
          window.cervusAccount?.close?.({ immediate: true });
          window.cervusNav?.close?.({ immediate: true });
        }
      }

      if (!open) {
        if (!isOpen) return Promise.resolve();
        return finishSearchClose(options);
      }

      if (isOpen) return Promise.resolve();

      isOpen = true;
      overlay.setAttribute('aria-hidden', 'false');
      if (!switchContext) {
        window.cervusBodyLock?.set?.('search', true);
      }
      openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });

      if (mobileMq.matches) {
        window.cervusHeaderScroll?.show?.();
        window.cervusNavChrome?.syncMobileHeaderTotalHeight?.();
        if (!switchContext) {
          syncNavSolid(true);
        }
        lastFocus = document.activeElement;
        window.cervusMobileDrawer?.openVisual?.(overlay);
        window.setTimeout(() => input?.focus(), reducedMotion ? 0 : 80);
        return Promise.resolve();
      }

      overlay.classList.add('is-open');
      window.cervusHeaderScroll?.show?.();
      syncNavSolid(true);
      lastFocus = document.activeElement;
      closeNavPanel({ immediate: true });
      window.setTimeout(() => input?.focus(), reducedMotion ? 0 : 80);
      return Promise.resolve();
    }

    function hideLiveResults() {
      if (!liveRoot || !liveList) return;
      liveRoot.hidden = true;
      liveList.innerHTML = '';
    }

    function escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
    }

    async function fetchSuggestions(query) {
      if (!query || query.length < 2) {
        hideLiveResults();
        return;
      }

      if (suggestRequest) suggestRequest.abort();
      suggestRequest = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,page,article,collection',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'hide',
      });

      try {
        const response = await fetch(`/search/suggest.json?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: suggestRequest.signal,
        });

        if (!response.ok) {
          hideLiveResults();
          return;
        }

        const payload = await response.json();
        renderLiveResults(payload);
      } catch (error) {
        if (error.name !== 'AbortError') hideLiveResults();
      }
    }

    function renderLiveResults(payload) {
      if (!liveRoot || !liveList) return;

      const resources = payload?.resources?.results || {};
      const items = [];

      ['products', 'collections', 'pages', 'articles'].forEach((type) => {
        const group = resources[type];
        if (!Array.isArray(group)) return;

        group.forEach((item) => {
          items.push({
            title: item.title,
            url: item.url,
            type,
          });
        });
      });

      if (!items.length) {
        hideLiveResults();
        return;
      }

      liveList.innerHTML = items
        .slice(0, 6)
        .map((item) => {
          const typeLabel =
            item.type === 'products'
              ? overlay.dataset.typeProduct
              : item.type === 'collections'
                ? overlay.dataset.typeCollection
                : item.type === 'articles'
                  ? overlay.dataset.typeArticle
                  : overlay.dataset.typePage;

          return `<li><a class="search-overlay__live-link type-ui" href="${escapeHtml(item.url)}"><span>${escapeHtml(item.title)}</span><span class="search-overlay__live-type">${escapeHtml(typeLabel || '')}</span></a></li>`;
        })
        .join('');

      liveRoot.hidden = false;
    }

    function isBareSearchLink(anchor) {
      if (!(anchor instanceof HTMLAnchorElement) || !nav?.contains(anchor)) return false;
      const url = new URL(anchor.href, window.location.origin);
      return url.pathname === searchPath && !url.searchParams.get('q');
    }

    openButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (mobileMq.matches && window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.toggle('search');
          return;
        }
        setSearchOpen(!isOpen);
      });
    });

    nav?.querySelectorAll('a[href]').forEach((anchor) => {
      if (!isBareSearchLink(anchor)) return;
      anchor.addEventListener('click', (event) => {
        event.preventDefault();
        setSearchOpen(true);
      });
    });

    closeTargets.forEach((target) => {
      target.addEventListener('click', () => {
        if (mobileMq.matches && window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('search');
          return;
        }
        setSearchOpen(false);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        if (mobileMq.matches && window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('search');
          return;
        }
        setSearchOpen(false);
      }
    });

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay.querySelector('.search-overlay__backdrop')) {
        if (mobileMq.matches && window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('search');
          return;
        }
        setSearchOpen(false);
      }
    });

    document.addEventListener('click', (event) => {
      if (!isOpen || !desktopMq.matches) return;
      if (overlay.contains(event.target)) return;
      if (event.target.closest('[data-search-open]')) return;
      setSearchOpen(false);
    });

    if (input) {
      input.addEventListener('input', () => {
        window.clearTimeout(suggestTimer);
        const query = input.value.trim();
        suggestTimer = window.setTimeout(() => fetchSuggestions(query), 280);
      });
    }

    if (form) {
      form.addEventListener('submit', (event) => {
        const query = input?.value.trim();
        if (!query) {
          event.preventDefault();
          input?.focus();
        }
      });
    }

    if (document.querySelector('[data-search-idle]')) {
      setSearchOpen(true);
    }

    window.cervusSearch = {
      open: () => setSearchOpen(true),
      close: (options) => setSearchOpen(false, options),
    };

    window.cervusMobileDrawerSwitch?.register?.('search', {
      isOpen: () => isOpen,
      open: async (options) => {
        await setSearchOpen(true, options);
      },
      close: async (options) => {
        await setSearchOpen(false, options);
      },
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cervusInitSearchOverlay);
  } else {
    cervusInitSearchOverlay();
  }
})();
